import {
  GiftDeliveryForAdmin,
  GiftDeliveryForRebatePending,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftRebateSettlementLine } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { SettlementPendingEntry } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { getWorkerDisplayName } from '@shared/utils/agencyWorkerUtils';

import { getItemName } from './delivery.utils';

// ─────────────────────────────────────────────
// 상조 리베이트 정산 테이블 — 행 계산 / 합계 유틸
//
// 정산 예정·배송완료 테이블이 동일한 컬럼/계산식을 공유한다.
// 가격 데이터는 모두 주문 스냅샷(giftOrder.orderDetails[].giftItemEmbed)에서 가져온다.
// ─────────────────────────────────────────────

/** 리베이트 기본 수수료율 (%) — 상조별 giftAccountConfig.feeRate 미설정 시 폴백 */
export const DEFAULT_REBATE_FEE_RATE = 5.3;

// ── 반올림 규칙 (2026-07-24 합의, 백엔드 gift-settlement.shared.ts와 동일) ──
// 끝수(1원 미만)는 건건이 우리(운영사) 몫: 지급액은 건별 내림, 수수료는 건별 올림.
// 판매가·도매가가 정수라 floorWon(지급액) = 판매가 − ceilFeeWon(수수료) − 도매가 (항등).
// 총액은 정수 라인의 합 — 총액 단계 반올림은 폐지됐다.

/** 지급액(수익금) 원 단위 내림 — 백엔드 floorWon과 동일 (+1e-6은 FP 잡음 가드) */
export const floorWon = (n: number): number => Math.floor(n + 1e-6);

/** 수수료 원 단위 올림 — floorWon의 쌍대 (−1e-6 FP 가드, +0은 −0 방지) */
export const ceilFeeWon = (n: number): number => Math.ceil(n - 1e-6) + 0;

/** 부동소수점 잡음 제거(소수 6자리 반올림) — 엑셀 셀 저장값 정리용. 정수는 정수로 남는다. */
export const cleanFloatNoise = (n: number): number => Math.round(n * 1e6) / 1e6;

/** 테이블 입력 한 건 (정산 예정/배송완료 공통) */
export interface RebateTableEntry {
  /** populate된 배송 데이터 */
  delivery: GiftDeliveryForRebatePending;
  /** 수량 (정산 예정: preview.quantity, 배송완료: delivery.quantity) */
  quantity: number;
  /** 정산 설정(상조 giftAccountConfig) 존재 여부 — 경고 배지 표시용 */
  hasSettlementInfo: boolean;
}

/** 테이블 한 행의 계산 결과 */
export interface RebateRowCalc {
  /** 행 선택용 키(미정산 집행 선택 시 사용). 배송 _id. 상세 모달 등 선택 불필요 시 미설정 */
  rowKey?: string;
  /** 상품명 */
  itemName: string;
  /** 상품 업체(공급사)명 — giftItemBrand.name. 없으면 undefined (표시 시 '-') */
  vendorName?: string;
  /** 구매자(주문자)명 */
  receiverName: string;
  /** 고인명 (giftOrder.bugo.fevent.deceasedInfo.name) */
  deceasedName?: string;
  /** 발인일 (giftOrder.bugo.fevent.deceasedInfo.coffinOut.date) */
  coffinOutDate?: Date;
  /** 지도사 — 닉네임(bugoAgencyWorkerDetail.nickName) 있으면 닉네임, 없으면 info.name */
  workerName?: string;
  /** 지도사 전화번호 (giftOrder.bugo.fevent.user.info.phoneNumber) */
  workerPhoneNumber?: string;
  /** 배송요청일 */
  submittedAt?: Date;
  /** 배송완료일 */
  deliveredAt?: Date;
  /** 판매가 (giftItemEmbed.priceRetail) */
  priceRetail: number | null;
  /**
   * 상품원가 (개당) — 소량주문 추가단가(extraUnitPrice)를 **포함한** 정산 기준 원가.
   * 백엔드가 소량추가분을 수익금(unitAmount)에서 차감하므로, 화면에선 그만큼 원가에 더해
   * 표시해야 "판매가 − 원가 − 수수료 = 수익금"이 맞아떨어진다(제시 도매가 + 소량추가).
   */
  priceWhole: number | null;
  /** 위 priceWhole에 포함된 소량주문 추가단가(개당). 없으면 undefined (표시 힌트용) */
  extraUnitPrice?: number;
  /**
   * 수수료 합계(수량 반영, 정수) = 결제금액 − 상품원가 − 수익금 합계.
   * 건별 올림(ceilFeeWon(수수료원값 × 수량))과 등가 — 백엔드 지급액(lineAmount 건별 내림)과
   * 1원 오차 없이 맞아떨어진다. FeeRate 방식만 계산, 계산 불가 시 null.
   */
  lineFee: number | null;
  /** 건당 수익금(절사 전 원값) = 판매가 − 상품원가 − 수수료원값. 감사·역산용 */
  unitAmount: number | null;
  /** 수량 */
  quantity: number;
  /** 수익금 합계(지급 기준, 정수) = floorWon(건당 수익금 × 수량) — 백엔드와 동일 */
  lineAmount: number | null;
  /** 정산 설정 존재 여부 */
  hasSettlementInfo: boolean;
}

// ── 소량주문 추가단가 — 상조정산과 무관 (2026-09-18 개정) ──
// 소량추가금은 구매자가 내고 업체가 받는 통과 금액이라 상조 판매가에도 원가에도 넣지 않는다
// (예전엔 원가에만 얹어 상조 수익금이 개당 그만큼 깎였다 — 백엔드 computeRebatePreview도 제거).
// 단, 개정 전에 동결된 정산 라인·구서버 preview에는 차감분이 unitAmount에 녹아 있으므로
// 아래 역산(backDerivePerUnitSurcharge)은 유지한다 — 새 라인에선 0이 나와 표시가 사라진다.

/**
 * 백엔드가 수익금(unitAmount)에 녹인 소량추가 개당분을 역산한다 (FeeRate·동결 라인용).
 *   unitAmount = 판매가 − 수수료 − (priceWhole + 소량추가)
 *   ⇒ 소량추가 = 판매가 − 수수료 − priceWhole − unitAmount
 * 구매 시점 동결 수수료(feePerUnit)가 있으면 전 항이 정수라 **정확히** 복원된다.
 * 없으면(레거시) feeRate 곱으로 역산 — 동결 수수료 시대의 라인에 요율 역산을 쓰면
 * 끝수(0.5원↑)가 반올림돼 +1원 오검될 수 있으므로 반드시 feePerUnit을 우선 전달할 것.
 * shippingFee는 정수 원이므로 부동소수 잡음은 반올림으로 제거하고, 음수는 0으로 가드한다.
 */
const backDerivePerUnitSurcharge = (
  priceRetail: number,
  priceWhole: number,
  unitAmount: number,
  feeRate: number,
  feePerUnit?: number,
): number => {
  const raw =
    feePerUnit != null
      ? priceRetail - feePerUnit - priceWhole - unitAmount
      : priceRetail * (1 - feeRate / 100) - priceWhole - unitAmount;
  return Math.max(0, Math.round(raw));
};

/**
 * 배송 1건 + 수량을 받아 테이블 한 행에 필요한 모든 값을 계산한다.
 *
 * - 가격은 주문 스냅샷(giftItemEmbed)에서만 읽는다.
 * - 수수료(FeeRate 방식만): 구매 시점 동결값(saleFeeCharge/feePerUnit — 정수)이 있으면
 *   계산 없이 그대로, 없으면(레거시) giftAccountConfig.feeRate(기본 5.3%)로 계산.
 * - 소량주문 추가단가는 미반영(구매자→업체 통과 금액, 백엔드와 동일).
 * - 수익금 합계 = floorWon((판매가 − 원가 − 수수료) × 수량) — 백엔드 preview와
 *   동일(동결 수수료면 전 항이 정수라 절사는 no-op).
 */
export const computeRebateRow = (entry: RebateTableEntry): RebateRowCalc => {
  const { delivery, quantity, hasSettlementInfo } = entry;
  const giftItem = delivery.giftItem;
  const cfg = giftItem?.bugoBrand?.giftAccountConfig;
  const method = cfg?.method ?? 'FeeRate';

  const orderDetail = delivery.giftOrder?.orderDetails?.find(
    (od) => String(od.giftItem) === String(giftItem?._id),
  );
  const embed = orderDetail?.giftItemEmbed;
  const priceRetail = embed?.priceRetail ?? null;
  const basePriceWhole = embed?.priceWhole ?? null;

  // 소량추가금은 상조정산에 미반영 (백엔드 computeRebatePreview와 동일) — 원가는 도매가 그대로
  const priceWhole = basePriceWhole;

  const feeRate = cfg?.feeRate ?? DEFAULT_REBATE_FEE_RATE;
  // 구매 시점 동결 수수료(단가당 정수) — 있으면 계산 없이 그대로 (배송 denormalize 우선,
  // 폴백 주문 embed). 없으면(레거시) 현재 요율 계산 — 백엔드 computeRebatePreview와 동일 위상.
  const frozenFee =
    method === 'FeeRate'
      ? delivery.saleFeeCharge ?? orderDetail?.giftItemEmbed?.feePerUnit ?? null
      : null;
  const feeRaw =
    method === 'FeeRate' && priceRetail != null
      ? frozenFee ?? priceRetail * (feeRate / 100)
      : null;

  const unitAmount =
    priceRetail != null && priceWhole != null && feeRaw != null
      ? priceRetail - priceWhole - feeRaw
      : null;

  // 지급액은 라인(건) 단위 절사 — 백엔드 computeRebatePreview의 floorWon(unitAmount × quantity)
  const lineAmount = unitAmount != null ? floorWon(unitAmount * quantity) : null;
  // 수수료 합계는 항등식으로 역산(= ceilFeeWon(수수료원값 × 수량)) — 지급액과 1원 오차 없음
  const lineFee =
    feeRaw == null
      ? null
      : lineAmount != null && priceRetail != null && priceWhole != null
        ? priceRetail * quantity - priceWhole * quantity - lineAmount
        : ceilFeeWon(feeRaw * quantity);

  // 고인명·발인일·지도사 (giftOrder.bugo.fevent populate 시에만 존재)
  const fevent = delivery.giftOrder?.bugo?.fevent;

  return {
    itemName: embed?.name ?? giftItem?.name ?? '-',
    vendorName: delivery.giftItemBrand?.name,
    receiverName: delivery.giftOrder?.receiverName || '-',
    deceasedName: fevent?.deceasedInfo?.name,
    coffinOutDate: fevent?.deceasedInfo?.coffinOut?.date,
    workerName: getWorkerDisplayName(fevent?.user),
    workerPhoneNumber: fevent?.user?.info?.phoneNumber,
    submittedAt: delivery.recipient?.submittedAt,
    deliveredAt: delivery.shipping?.deliveredAt,
    priceRetail,
    priceWhole,
    lineFee,
    unitAmount,
    quantity,
    lineAmount,
    hasSettlementInfo,
  };
};

/** 테이블 합계 — 전 항목 건별 반올림된 정수 라인의 합 (총액 단계 반올림 없음) */
export interface RebateTotals {
  /** 건수 */
  totalCount: number;
  /** 총 수량 */
  totalQuantity: number;
  /** 총 수익금 = 정수 lineAmount의 합 (= 총판매가 − 총상품원가 − 총수수료, 건별 항등식으로 보장) */
  totalAmount: number;
  /** 판매가 합 (판매가 × 수량의 합) */
  totalPriceRetail: number;
  /** 상품원가 합 (원가 × 수량의 합) */
  totalPriceWhole: number;
  /** 수수료 합 = 정수 lineFee(건별 올림)의 합 */
  totalFee: number;
  /** 정산 설정 미설정 건수 */
  missingSettlementCount: number;
}

/** 계산된 행 목록으로부터 합계를 집계한다. */
export const computeRebateTotals = (rows: RebateRowCalc[]): RebateTotals => {
  const totalPriceRetail = rows.reduce(
    (sum, r) => sum + (r.priceRetail ?? 0) * r.quantity,
    0,
  );
  const totalPriceWhole = rows.reduce(
    (sum, r) => sum + (r.priceWhole ?? 0) * r.quantity,
    0,
  );
  // 건별 정수 값의 합 — 서버 정산 총액(정수 라인의 합)과 1원 오차 없이 일치한다.
  const totalFee = rows.reduce((sum, r) => sum + (r.lineFee ?? 0), 0);
  const totalAmount = rows.reduce((sum, r) => sum + (r.lineAmount ?? 0), 0);

  return {
    totalCount: rows.length,
    totalQuantity: rows.reduce((sum, r) => sum + r.quantity, 0),
    totalAmount,
    totalPriceRetail,
    totalPriceWhole,
    totalFee,
    missingSettlementCount: rows.filter((r) => !r.hasSettlementInfo).length,
  };
};

/**
 * 정산 문서의 라인들로부터 "지급 예정액"을 계산한다 = 건별 내림(floorWon) 라인의 합.
 *
 * 신규(2026-07 반올림 개정 후) 동결 라인은 이미 정수라 no-op이고, 개정 전 Draft 문서의
 * 소수 잔재만 지급액 방향(내림)으로 정리한다 — 백엔드가 다음 편집/지급확정 때 재동결하는
 * 값과 동일한 예측치다. Paid(완료)는 동결·지급된 `totalAmount`를 그대로 쓴다(이 함수 사용 X).
 */
export const computeRebatePayableFromLines = (
  lines: GiftRebateSettlementLine[],
): number => lines.reduce((sum, l) => sum + floorWon(l.lineAmount), 0);

/**
 * 테이블 행을 배송완료일 오름차순으로 정렬한다(가장 오래된 건이 맨 위 = 1번).
 * 완료일이 없는 건은 맨 뒤로 보낸다. 원본 배열은 변경하지 않는다(복사 후 정렬).
 */
export const sortRebateRowsByDeliveredAt = (rows: RebateRowCalc[]): RebateRowCalc[] =>
  [...rows].sort((a, b) => {
    const ta = a.deliveredAt ? new Date(a.deliveredAt).getTime() : Infinity;
    const tb = b.deliveredAt ? new Date(b.deliveredAt).getTime() : Infinity;
    return ta - tb;
  });

// ── 정산 라인 → 테이블 행 매핑 (정산 예정 / 완료 상세 모달 공유) ──

/** 장례 이벤트(fevent) — 고인명/발인일/지도사 표시용 */
interface RebateFevent {
  deceasedInfo?: { name?: string; coffinOut?: { date?: Date } };
  /** 담당 지도사 */
  user?: {
    info?: { name?: string; phoneNumber?: string };
    bugoAgencyWorkerDetail?: { nickName?: string };
  };
}

/**
 * /gift-rebate-settlement 응답의 line.giftDelivery 실제 populate 형태.
 * fevent 는 백엔드 버전에 따라 giftOrder.fevent 또는 giftOrder.bugo.fevent 로 올 수 있어
 * 양쪽을 모두 옵셔널로 둔다.
 */
interface RebateLineDelivery {
  recipient?: { submittedAt?: Date };
  shipping?: { deliveredAt?: Date };
  /** 구매 시점 동결 단가당 수수료(원, 정수) — 있으면 소량추가 역산이 정확해진다 */
  saleFeeCharge?: number;
  /** 정산 방식/요율 — 소량추가 역산용 (bugoAgencyDeliveryPopulate로 함께 populate됨) */
  giftItem?: {
    bugoBrand?: {
      giftAccountConfig?: { method?: 'FeeRate' | 'Rebate'; feeRate?: number };
    };
  };
  giftOrder?: {
    receiverName?: string;
    user?: { info?: { name?: string } };
    fevent?: RebateFevent;
    bugo?: { fevent?: RebateFevent };
  };
}

/**
 * 정산 문서의 라인 1건 → 테이블 행(RebateRowCalc).
 * - 상품명/금액: 집행 시 동결된 라인 스냅샷(line.item, line.unitAmount, line.lineAmount)
 * - 배송 메타(구매자/배송일/고인명/발인일/지도사): populate된 line.giftDelivery
 *
 * 정산 예정 테이블과 완료 내역 상세 모달이 동일하게 사용한다.
 */
export const settlementLineToRebateRow = (
  line: GiftRebateSettlementLine,
): RebateRowCalc => {
  const gd =
    typeof line.giftDelivery === 'object' && line.giftDelivery
      ? (line.giftDelivery as unknown as RebateLineDelivery)
      : undefined;
  // fevent: 백엔드 버전에 따라 giftOrder.fevent 또는 giftOrder.bugo.fevent
  const fevent = gd?.giftOrder?.fevent ?? gd?.giftOrder?.bugo?.fevent;

  const priceRetail = line.item.priceRetail ?? null;
  const basePriceWhole = line.item.priceWhole ?? null;

  // 정산 방식/요율 — populate된 배송의 상조 설정에서 (없으면 폴백·기존 동작)
  const cfg = gd?.giftItem?.bugoBrand?.giftAccountConfig;
  const isRebateMethod = cfg?.method === 'Rebate';
  const feeRate = cfg?.feeRate ?? DEFAULT_REBATE_FEE_RATE;

  let priceWhole = basePriceWhole;
  let extraUnitPrice = 0;
  if (!isRebateMethod && priceRetail != null && basePriceWhole != null) {
    // FeeRate: 소량추가는 동결 unitAmount(절사 전 원값)에서 역산해 원가에 가산.
    // (백엔드가 소량추가를 unitAmount에 녹였기 때문에, 역산하지 않으면 수수료에 흡수돼 부풀려진다.)
    // 구매 시점 동결 수수료(gd.saleFeeCharge)가 있으면 전 항 정수라 역산이 정확하다.
    extraUnitPrice = backDerivePerUnitSurcharge(
      priceRetail,
      basePriceWhole,
      line.unitAmount,
      feeRate,
      gd?.saleFeeCharge,
    );
    priceWhole = basePriceWhole + extraUnitPrice;
  }

  // 금액은 항상 동결 lineAmount를 신뢰한다(unitAmount × quantity 재계산 금지 — 끝수가 다르다).
  // floorWon은 신규(정수) 라인엔 no-op, 반올림 개정 전 Draft 잔재만 지급액 방향으로 정리.
  const lineAmount = floorWon(line.lineAmount);
  // 수수료 합계(수량 반영)는 항등식으로 역산 — 표시 컬럼끼리 정확히 맞아떨어진다.
  // (Rebate 방식은 리베이트를 뺀 나머지 = 우리 마진을 수수료 칸에 보여주는 기존 표기 유지)
  const lineFee =
    priceRetail != null && priceWhole != null
      ? priceRetail * line.quantity - priceWhole * line.quantity - lineAmount
      : null;

  // 업체명: populate된 giftItemBrand.name 우선, 없으면 동결 brandName 폴백
  const brand = line.item.giftItemBrand;
  const vendorName =
    (typeof brand === 'object' && brand?.name) || line.item.brandName || undefined;

  return {
    itemName: line.item.name ?? '-',
    vendorName,
    receiverName: gd?.giftOrder?.user?.info?.name || gd?.giftOrder?.receiverName || '-',
    deceasedName: fevent?.deceasedInfo?.name,
    coffinOutDate: fevent?.deceasedInfo?.coffinOut?.date,
    workerName: getWorkerDisplayName(fevent?.user),
    workerPhoneNumber: fevent?.user?.info?.phoneNumber,
    submittedAt: gd?.recipient?.submittedAt,
    deliveredAt: gd?.shipping?.deliveredAt,
    priceRetail,
    priceWhole,
    extraUnitPrice: extraUnitPrice || undefined,
    lineFee,
    unitAmount: line.unitAmount,
    quantity: line.quantity,
    lineAmount,
    hasSettlementInfo: true,
  };
};

// ── 미정산(예정) 매퍼 / 정산단가 분해 ──

// 상조 정산단가 분해(판매가 − 도매가 − 수수료).
// 백엔드 preview(p.unitAmount)와 동일한 소스로 계산해 "판매가 − 도매가 − 수수료 = 정산단가"가
// 항상 맞아떨어지게 한다(판매가: 주문 스냅샷 우선, 도매가: giftItem.priceWhole — settle 동결 기준).
export interface RebateBreakdown {
  priceRetail: number | null;
  priceWhole: number | null;
  /**
   * FeeRate 방식 건당 수수료. 구매 시점 동결값(feePerUnit — 정수)이 있으면 그 값,
   * 없으면(레거시) 판매가 × feeRate% 원값. Rebate 방식이면 null.
   */
  fee: number | null;
  /** 구매 시점 동결 단가당 수수료(원, 정수) — 소량추가 정확 역산용. 레거시엔 undefined */
  feePerUnit?: number;
  feeRate: number;
  isRebateMethod: boolean;
}

export const getRebateBreakdown = (d: GiftDeliveryForAdmin): RebateBreakdown => {
  const gi = typeof d.giftItem === 'object' ? (d.giftItem as any) : undefined;
  const cfg = gi?.bugoBrand?.giftAccountConfig;
  const feeRate = cfg?.feeRate ?? DEFAULT_REBATE_FEE_RATE;
  const isRebateMethod = cfg?.method === 'Rebate';
  const order = typeof d.giftOrder === 'object' ? (d.giftOrder as any) : undefined;
  const detail = order?.orderDetails?.find(
    (od: any) => String(od.giftItem) === String(gi?._id),
  );
  const priceRetail = detail?.giftItemEmbed?.priceRetail ?? gi?.priceRetail ?? null;
  const priceWhole = gi?.priceWhole ?? gi?.giftItemInfo?.priceWhole ?? null;
  // 구매 시점 동결 수수료 — 배송 denormalize 우선, 주문 embed 폴백 (백엔드와 동일 사슬)
  const feePerUnit = d.saleFeeCharge ?? detail?.giftItemEmbed?.feePerUnit;
  const fee = isRebateMethod
    ? null
    : feePerUnit ??
      (priceRetail != null ? (priceRetail * feeRate) / 100 : null);
  return { priceRetail, priceWhole, fee, feePerUnit, feeRate, isRebateMethod };
};

// 미정산(예정) 배송 1건 → 상조정산 공용 테이블(RebateSettlementTable)의 행.
// 상세 페이지와 동일한 컬럼/계산을 쓰되, 도매가는 정산시점 라이브 값(getRebateBreakdown,
// = 백엔드 preview 기준)을 그대로 사용해 표시 금액이 preview와 어긋나지 않게 한다.
export const pendingEntryToRebateRow = (entry: SettlementPendingEntry): RebateRowCalc => {
  const d = entry.delivery;
  const p = entry.preview;
  const b = getRebateBreakdown(d);
  const order = typeof d.giftOrder === 'object' ? (d.giftOrder as any) : undefined;
  // fevent는 백엔드 버전에 따라 giftOrder.fevent 또는 giftOrder.bugo.fevent 로 온다
  const fevent = order?.fevent ?? order?.bugo?.fevent;

  // 소량추가: 백엔드 preview.unitAmount는 소량분이 이미 차감된 값이므로 역산해
  // 원가에 가산 표시한다(원가/수수료/수익금이 preview와 정확히 reconcile됨). FeeRate 방식 한정.
  // 동결 수수료(b.feePerUnit)가 있으면 전 항 정수라 역산이 정확하다.
  const extraUnitPrice =
    !b.isRebateMethod && b.priceRetail != null && b.priceWhole != null
      ? backDerivePerUnitSurcharge(
          b.priceRetail,
          b.priceWhole,
          p.unitAmount,
          b.feeRate,
          b.feePerUnit,
        )
      : 0;
  const priceWhole = b.priceWhole != null ? b.priceWhole + extraUnitPrice : b.priceWhole;

  // 수수료 합계 — 서버 preview.lineAmount(건별 내림 정수)와의 항등식으로 역산.
  // 판매가/원가를 모르면 원값 × 수량을 건별 올림(폴백 — 항등식과 동일 규칙).
  const lineFee = b.isRebateMethod
    ? null
    : b.priceRetail != null && priceWhole != null
      ? b.priceRetail * p.quantity - priceWhole * p.quantity - p.lineAmount
      : b.fee != null
        ? ceilFeeWon(b.fee * p.quantity)
        : null;

  return {
    rowKey: d._id,
    itemName: getItemName(d),
    vendorName:
      typeof d.giftItemBrand === 'object' ? d.giftItemBrand?.name : undefined,
    receiverName: order?.receiverName || d.recipient?.name || '-',
    deceasedName: fevent?.deceasedInfo?.name,
    coffinOutDate: fevent?.deceasedInfo?.coffinOut?.date,
    workerName: getWorkerDisplayName(fevent?.user),
    workerPhoneNumber: fevent?.user?.info?.phoneNumber,
    submittedAt: d.recipient?.submittedAt,
    deliveredAt: d.shipping?.deliveredAt,
    priceRetail: b.priceRetail,
    priceWhole,
    extraUnitPrice: extraUnitPrice || undefined,
    lineFee,
    unitAmount: p.unitAmount,
    quantity: p.quantity,
    lineAmount: p.lineAmount,
    hasSettlementInfo: p.hasSettlementInfo,
  };
};
